// 🔀 장표 종류 전환 — «여러 항목을 담는 4종» 사이의 내용 옮기기(2026-09-18 · 삼성 QA 요청).
//
// ★왜 이 4종만인가★ — 비교표·체크 리스트·단계 흐름·카드 그리드는 **«제목 + 여러 항목»** 이라는 같은
//   뼈대를 공유한다. 나머지 6종(전체 문구·모션 타이포·강조 숫자·좌우 강조·수치 행·이미지+캡션)은 구조가
//   달라 항목이 통째로 사라진다 — 요청서의 «손실이 큰 조합은 비활성화하고 사유를 표시» 를 따른다.
//   ★전체 문구 ↔ 모션 타이포★ 는 이 표와 **별개의 창구**(slide-auto.MOTION_TOGGLE_TYPES)로 이미 열려 있다.
//
// ★설계 한 줄★ — 모든 전환은 **중간 형태(제목 + 항목 목록)** 를 거친다. 4종 × 4종을 12벌 따로 쓰면
//   한쪽만 고쳐지는 날이 온다. 들어오는 길 4개 · 나가는 길 4개만 맞으면 모든 조합이 맞는다.
//
// ★미리보기와 적용은 같은 함수★ — 화면이 «이렇게 바뀝니다» 를 다른 계산으로 그리면 둘이 갈린다.
//   `convertSlideContent` 하나가 «바뀐 내용» 과 «빠지는 것» 을 함께 돌려주고, 미리보기도 적용도 그걸 쓴다.
import { SLIDE_ARRAY_CAPS } from "./slide-edit-contract";

/** 🔀 서로 바꿀 수 있는 장표 종류(v1). 순서 = 화면 목록 순서. */
export const SLIDE_SWITCH_TYPES = ["comparePair", "cardGrid", "checkList", "stepFlow"] as const;
export type SlideSwitchType = (typeof SLIDE_SWITCH_TYPES)[number];

const SWITCH_SET: ReadonlySet<string> = new Set(SLIDE_SWITCH_TYPES);

/** 🔀 이 짝이 열려 있는가. ★같은 종류로 바꾸기는 «전환» 이 아니다★(무변경이라 되돌리기 칸만 먹는다). */
export const canSwitchSlideType = (from: string, to: string): boolean =>
  SWITCH_SET.has(from) && SWITCH_SET.has(to) && from !== to;

/** 종류별 «담을 수 있는 항목 수». 비교표는 좌·우 두 면뿐이다. */
export const SLIDE_SWITCH_CAPACITY: Readonly<Record<SlideSwitchType, number>> = {
  comparePair: 2,
  cardGrid: SLIDE_ARRAY_CAPS.cards,
  checkList: SLIDE_ARRAY_CAPS.items,
  stepFlow: SLIDE_ARRAY_CAPS.steps,
};

/** 종류별 «항목 안에 본문 줄을 담을 수 있는가» — 못 담으면 본문이 누락 고지 대상이다. */
const KEEPS_BODY: Readonly<Record<SlideSwitchType, boolean>> = {
  comparePair: true, cardGrid: true, checkList: false, stepFlow: false,
};
/** 종류별 «항목에 배지를 담을 수 있는가». */
const KEEPS_BADGE: Readonly<Record<SlideSwitchType, boolean>> = {
  comparePair: false, cardGrid: true, checkList: false, stepFlow: false,
};

/** 그 종류가 쓰는 **글 필드** — 전환할 때 원본 쪽 키는 지운다(남겨 두면 되돌릴 때 옛 내용이 되살아나
 *  그 사이 고친 것을 덮는다). 연출 키(anchor·revealMode·keyword·cardStyle·cols…)는 건드리지 않는다. */
const TEXT_KEYS: Readonly<Record<SlideSwitchType, readonly string[]>> = {
  comparePair: ["title", "bad", "good"],
  cardGrid: ["title", "cards"],
  checkList: ["title", "items"],
  stepFlow: ["title", "steps"],
};

/** 중간 형태의 항목 하나.
 *  `badgeAuto` = 그 배지를 **유저가 넣은 게 아니라 전환이 만들어 준 것**(비교표의 ✕·✓). 누락 고지 문구가
 *  달라진다 — «1번 항목의 배지 ✕ 가 빠집니다» 는 배지를 넣은 적 없는 사람에게 무슨 말인지 모를 소리다. */
type Item = { title: string; body: string[]; badge?: string; badgeAuto?: boolean };

const str = (v: unknown): string => (typeof v === "string" ? v : v == null ? "" : String(v));
const strArr = (v: unknown): string[] => (Array.isArray(v) ? v.map(str).filter((s) => s.trim() !== "") : []);
const obj = (v: unknown): Record<string, unknown> => (v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : {});

/** 짧게 인용 — 누락 고지에 원문을 그대로 길게 실으면 읽기 어렵다. */
const quote = (s: string): string => {
  const t = s.trim().replace(/\s+/g, " ");
  return t.length <= 18 ? `«${t}»` : `«${t.slice(0, 18)}…»`;
};

/** 📥 들어오는 길 — 어떤 종류든 «제목 + 항목 목록» 으로 편다. */
function toItems(type: SlideSwitchType, content: Record<string, unknown>): { title: string; items: Item[] } {
  const title = str(content.title);
  if (type === "checkList") return { title, items: strArr(content.items).map((t) => ({ title: t, body: [] })) };
  if (type === "stepFlow") return { title, items: strArr(content.steps).map((t) => ({ title: t, body: [] })) };
  if (type === "cardGrid") {
    const cards = Array.isArray(content.cards) ? content.cards : [];
    return {
      title,
      items: cards.map((c) => {
        const o = obj(c);
        const badge = str(o.badge).trim();
        return { title: str(o.title), body: strArr(o.body), ...(badge ? { badge } : {}) };
      }).filter((i) => i.title.trim() !== "" || i.body.length > 0),
    };
  }
  // comparePair — 좌(나쁨)·우(좋음) 두 면. ★배지로 «✕/✓» 를 만들어 준다★: 비교표는 그 표식을 렌더러가
  //   직접 그리므로 내용에 없다. 그냥 옮기면 «어느 쪽이 좋은 쪽인지» 가 사라진다(카드 그리드엔 표식이 없다).
  const bad = obj(content.bad);
  const good = obj(content.good);
  const side = (o: Record<string, unknown>, badge: string): Item => ({ title: str(o.title), body: strArr(o.body), badge, badgeAuto: true });
  return { title, items: [side(bad, "✕"), side(good, "✓")].filter((i) => i.title.trim() !== "" || i.body.length > 0) };
}

/** 📤 나가는 길 — 항목 목록을 그 종류의 글 필드로 담고, 못 담은 것을 고지 문구로 남긴다. */
function fromItems(type: SlideSwitchType, title: string, items: Item[], colsOf: 2 | 3 = 2): { fields: Record<string, unknown>; dropped: string[] } {
  const cap = SLIDE_SWITCH_CAPACITY[type];
  const kept = items.slice(0, cap);
  const dropped: string[] = [];
  for (const [i, it] of items.slice(cap).entries()) {
    dropped.push(`${cap + i + 1}번째 항목 ${quote(it.title || it.body[0] || "")}`);
  }
  if (!KEEPS_BADGE[type]) {
    // 전환이 만들어 준 ✕·✓ 는 «좋음·나쁨 표시» 라는 한 줄로 묶는다(항목마다 적으면 남의 말처럼 읽힌다).
    if (kept.some((it) => it.badge && it.badgeAuto)) dropped.push("좋음·나쁨 표시(✕·✓)");
    for (const [i, it] of kept.entries()) if (it.badge && !it.badgeAuto) dropped.push(`${i + 1}번 항목의 배지 ${quote(it.badge)}`);
  }
  if (!KEEPS_BODY[type]) {
    for (const [i, it] of kept.entries()) if (it.body.length) dropped.push(`${i + 1}번 항목의 본문 ${it.body.length}줄`);
  }

  if (type === "checkList") return { fields: { title, items: kept.map((i) => i.title) }, dropped };
  if (type === "stepFlow") return { fields: { title, steps: kept.map((i) => i.title) }, dropped };
  if (type === "cardGrid") {
    const cards = kept.map((i) => {
      const body = i.body.slice(0, SLIDE_ARRAY_CAPS.body);
      if (i.body.length > body.length) dropped.push(`${quote(i.title)} 카드의 본문 ${i.body.length - body.length}줄`);
      return { ...(i.badge ? { badge: i.badge } : {}), title: i.title, ...(body.length ? { body } : {}) };
    });
    // ★`cols` 를 반드시 싣는다★ — story 트랙(Overlay169)은 content 를 **그대로 스프레드**해서
    //   컴포넌트에 넘긴다(MSB 트랙의 `cols={n(c.cols)===3?3:2}` 같은 보정이 없다). 없으면 CardGrid 의
    //   `Math.min(cols, 3)` 이 NaN 이 되어 카드가 쪼그라들어 한쪽으로 밀린다(09-18 렌더 실측:
    //   글자·판 픽셀 20.25% ↔ cols 2 를 주면 34.13%). 원본이 갖고 있던 2·3 은 그대로 존중한다.
    return { fields: { title, cards, cols: colsOf }, dropped };
  }
  // comparePair — 두 면은 «있어야 하는 자리» 라서 모자라면 빈 칸으로 만든다(렌더러가 빈 면을 그리는 것이
  //   내용을 말없이 지우는 것보다 낫다 · 유저가 바로 채울 수 있다).
  const side = (i: Item | undefined) => {
    const body = (i?.body ?? []).slice(0, SLIDE_ARRAY_CAPS.body);
    if (i && i.body.length > body.length) dropped.push(`${quote(i.title)} 쪽 본문 ${i.body.length - body.length}줄`);
    return { title: i?.title ?? "", ...(body.length ? { body } : {}) };
  };
  if (kept.length < 2) dropped.push(kept.length === 0 ? "양쪽 칸이 모두 빕니다" : "오른쪽 칸이 빕니다");
  return { fields: { title, bad: side(kept[0]), good: side(kept[1]) }, dropped };
}

export type SlideConvertResult = {
  /** 바뀐 뒤의 content 전체(연출 키는 그대로 보존, 원본 종류의 글 필드는 제거). */
  content: Record<string, unknown>;
  /** 사람이 읽는 «빠지는 것» 목록. 비었으면 손실 없음. */
  dropped: string[];
};

/**
 * 🔀 장표 내용을 다른 종류로 옮긴다. 열리지 않은 짝이면 `null`(호출부는 무변경으로 떨어진다).
 * ★순수 함수★ — 같은 입력은 언제나 같은 출력이라 미리보기와 적용이 갈릴 수 없다.
 */
export function convertSlideContent(
  from: string,
  to: string,
  content: Record<string, unknown>,
): SlideConvertResult | null {
  if (!canSwitchSlideType(from, to)) return null;
  const fromT = from as SlideSwitchType;
  const toT = to as SlideSwitchType;
  const { title, items } = toItems(fromT, content);
  // 원본이 이미 갖고 있는 칸 수(2·3)는 존중한다 — 그 밖의 값·부재는 2 로 떨어진다(MSB 보정과 같은 규율).
  const cols = content.cols === 3 || content.cols === "3" ? 3 : 2;
  const { fields, dropped } = fromItems(toT, title, items, cols);
  // 연출 키는 남긴다 — 원본·목표 양쪽의 «글 필드» 만 걷어내고 새 글 필드를 얹는다.
  const rest: Record<string, unknown> = { ...content };
  for (const k of [...TEXT_KEYS[fromT], ...TEXT_KEYS[toT]]) delete rest[k];
  return { content: { ...rest, ...fields }, dropped };
}
